import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  Chip,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import LabelsService from '../../services/LabelsService';

const STORAGE_KEY = 'doctor_interests_assigned';

export const getDoctorInterestsStorageKey = (userId) => `${STORAGE_KEY}_${userId}`;

const DoctorInterestsModal = ({ open, onClose, userId, onSuccess }) => {
  const [labels, setLabels] = useState([]);
  const [labelGroups, setLabelGroups] = useState([]);
  const [userLabels, setUserLabels] = useState([]);
  const [labelGroupDetails, setLabelGroupDetails] = useState({});
  const [labelGroupAssigning, setLabelGroupAssigning] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [labelGroupMessage, setLabelGroupMessage] = useState(null);
  const [audienceTargets, setAudienceTargets] = useState({});

  const profileLabels = labels.filter((l) => l.isProfile === true);
  const normalLabels = labels.filter((l) => l.isProfile !== true);

  const refreshUserLabels = async () => {
    if (!userId) return;
    try {
      const etiquetasUsuario = await LabelsService.getUserLabels(userId);
      setUserLabels(etiquetasUsuario || []);
      const targets = {};
      (etiquetasUsuario || []).forEach((etiqueta) => {
        targets[etiqueta.labelId] = true;
      });
      setAudienceTargets(targets);
    } catch (err) {
      setUserLabels([]);
    }
  };

  useEffect(() => {
    if (!open || !userId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [labelsData, groupsData] = await Promise.all([
          LabelsService.getAllLabels(),
          LabelsService.getLabelGroups(),
        ]);
        setLabels(labelsData || []);
        setLabelGroups(groupsData || []);

        await refreshUserLabels();
      } catch (err) {
        setError(err.message || 'Error al cargar etiquetas');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, userId]);

  const handleProfileToggle = async (labelId, isChecked) => {
    if (!userId) return;

    if (!isChecked) {
      const userLabel = userLabels.find((ul) => ul.labelId === labelId);
      if (!userLabel) {
        setAudienceTargets((prev) => ({ ...prev, [labelId]: false }));
        return;
      }
      try {
        setSubmitting(true);
        await LabelsService.deleteUserLabel(userLabel.id);
        await refreshUserLabels();
      } catch (err) {
        setError(err.message || 'Error al eliminar el perfil');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      setSubmitting(true);
      const profileLabelIds = new Set(profileLabels.map((l) => l.id));
      const existingProfileUserLabels = userLabels.filter((ul) =>
        profileLabelIds.has(ul.labelId)
      );

      if (existingProfileUserLabels.length > 0) {
        await Promise.all(
          existingProfileUserLabels.map((ul) =>
            LabelsService.deleteUserLabel(ul.id)
          )
        );
      }

      await LabelsService.saveUserLabel(userId, labelId);
      await refreshUserLabels();
    } catch (err) {
      setError(err.message || 'Error al asignar el perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLabel = async (labelId) => {
    if (!userId) return;
    try {
      setSubmitting(true);
      await LabelsService.saveUserLabel(userId, labelId);
      setAudienceTargets((prev) => ({ ...prev, [labelId]: true }));
      await refreshUserLabels();
    } catch (err) {
      setError(err.message || 'Error al agregar la etiqueta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveLabel = async (labelId) => {
    if (!userId) return;
    const userLabel = userLabels.find((ul) => ul.labelId === labelId);
    if (!userLabel) return;
    try {
      setSubmitting(true);
      await LabelsService.deleteUserLabel(userLabel.id);
      setAudienceTargets((prev) => ({ ...prev, [labelId]: false }));
      await refreshUserLabels();
    } catch (err) {
      setError(err.message || 'Error al eliminar la etiqueta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleLabelGroupDetails = async (groupId) => {
    const currentDetails = labelGroupDetails[groupId];
    if (currentDetails?.open) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: { ...currentDetails, open: false },
      }));
      return;
    }

    if (currentDetails?.data) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: { ...currentDetails, open: true },
      }));
      return;
    }

    setLabelGroupDetails((prev) => ({
      ...prev,
      [groupId]: { ...currentDetails, open: true, loading: true },
    }));

    try {
      const data = await LabelsService.getLabelGroupWithLabels(groupId);
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: { open: true, loading: false, data },
      }));
    } catch (err) {
      setLabelGroupDetails((prev) => ({
        ...prev,
        [groupId]: { open: true, loading: false, error: true },
      }));
    }
  };

  const handleAssignLabelGroup = async (group) => {
    if (!userId) return;
    try {
      setLabelGroupAssigning((prev) => ({ ...prev, [group.id]: true }));
      setLabelGroupMessage(null);
      await LabelsService.assignLabelGroups(userId, [group.id]);
      await refreshUserLabels();
      setLabelGroupMessage({
        type: 'success',
        text: `Grupo "${group.name}" asignado correctamente.`,
      });
    } catch (err) {
      setLabelGroupMessage({
        type: 'error',
        text: err.message || `No se pudo asignar el grupo "${group.name}".`,
      });
    } finally {
      setLabelGroupAssigning((prev) => ({ ...prev, [group.id]: false }));
    }
  };

  const handleFinish = () => {
    const hasProfile = profileLabels.some((l) => audienceTargets[l.id]);
    if (!hasProfile) {
      setError('Selecciona tu perfil profesional (rol)');
      return;
    }
    localStorage.setItem(getDoctorInterestsStorageKey(userId), 'true');
    typeof onSuccess === 'function' && onSuccess();
    onClose();
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Configura tus intereses
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Selecciona tu perfil, especialidades y grupos. Solo necesitas hacerlo
          una vez.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#4DABA4' }} />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {labelGroupMessage && (
              <Alert
                severity={labelGroupMessage.type}
                onClose={() => setLabelGroupMessage(null)}
              >
                {labelGroupMessage.text}
              </Alert>
            )}

            {/* Perfil profesional - mismo que RegristerForm */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                👤 Perfil Profesional
              </Typography>
              <Stack spacing={1}>
                {profileLabels.map((label) => (
                  <FormControlLabel
                    key={label.id}
                    control={
                      <Radio
                        checked={!!audienceTargets[label.id]}
                        onChange={(e) =>
                          handleProfileToggle(label.id, e.target.checked)
                        }
                        disabled={submitting}
                        sx={{
                          color: '#4DABA4',
                          '&.Mui-checked': { color: '#4DABA4' },
                        }}
                      />
                    }
                    label={label.name}
                  />
                ))}
              </Stack>
            </Box>

            {/* Especialidades - mismo patrón que RegristerForm con handleAddLabel/handleRemoveLabel */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                🏷️ Especialidades
              </Typography>
              <Box sx={{ mb: 1 }}>
                {normalLabels
                  .filter((l) => audienceTargets[l.id])
                  .map((label) => (
                    <Chip
                      key={label.id}
                      label={label.name}
                      size="small"
                      onDelete={() => handleRemoveLabel(label.id)}
                      deleteIcon={<RemoveIcon />}
                      sx={{
                        m: 0.5,
                        fontWeight: 600,
                        backgroundColor: '#4DABA4',
                        color: 'white',
                      }}
                    />
                  ))}
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {normalLabels
                  .filter((l) => !audienceTargets[l.id])
                  .map((label) => (
                    <Button
                      key={label.id}
                      variant="outlined"
                      size="small"
                      onClick={() => handleAddLabel(label.id)}
                      disabled={submitting}
                      startIcon={<AddIcon />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#d1d5db',
                        color: '#374151',
                        '&:hover': {
                          borderColor: '#4DABA4',
                          backgroundColor: 'rgba(77, 171, 164, 0.05)',
                        },
                      }}
                    >
                      {label.name}
                    </Button>
                  ))}
              </Stack>
            </Box>

            {/* Grupos de etiquetas - mismo que RegristerForm con assignLabelGroups */}
            {labelGroups.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5 }}
                >
                  🧩 Grupos de etiquetas
                </Typography>
                <Stack spacing={1.5}>
                  {labelGroups.map((group) => {
                    const details = labelGroupDetails[group.id];
                    const labelsInGroup = details?.data?.labels || [];

                    return (
                      <Box
                        key={group.id}
                        sx={{
                          p: 1.5,
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {group.name}
                            </Typography>
                            {group.description && (
                              <Typography variant="caption" color="text.secondary">
                                {group.description}
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              size="small"
                              onClick={() =>
                                handleToggleLabelGroupDetails(group.id)
                              }
                              disabled={details?.loading}
                              sx={{
                                textTransform: 'none',
                                color: '#4DABA4',
                              }}
                            >
                              {details?.open
                                ? 'Ocultar'
                                : 'Ver etiquetas'}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleAssignLabelGroup(group)}
                              disabled={labelGroupAssigning[group.id]}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: '#4DABA4',
                                '&:hover': { backgroundColor: '#3e958e' },
                              }}
                            >
                              {labelGroupAssigning[group.id]
                                ? 'Asignando...'
                                : 'Asignar'}
                            </Button>
                          </Stack>
                        </Stack>
                        {details?.open && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: '#f8fafc',
                              borderRadius: 1,
                            }}
                          >
                            {details?.loading ? (
                              <CircularProgress size={16} />
                            ) : details?.error ? (
                              <Typography variant="caption" color="error">
                                Error al cargar
                              </Typography>
                            ) : labelsInGroup.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                Sin etiquetas
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {labelsInGroup.map((label, idx) => (
                                  <Chip
                                    key={`${label.name}-${idx}`}
                                    label={label.name}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleFinish}
          disabled={loading || submitting}
          sx={{
            backgroundColor: '#4DABA4',
            '&:hover': { backgroundColor: '#3e958e' },
          }}
        >
          Finalizar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoctorInterestsModal;
